import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Users, GraduationCap, UserCheck, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KpiCard({ icon: Icon, label, value, color = '#0047FF' }) {
  return (
    <div className="bg-white border border-[#E5E7EB] p-6 shadow-none" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 flex items-center justify-center" style={{ backgroundColor: `${color}10` }}>
          <Icon size={18} style={{ color }} />
        </div>
        <span className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">{label}</span>
      </div>
      <p className="font-heading text-3xl font-black text-[#0A0A0A]">{value}</p>
    </div>
  );
}

function TraineeRow({ trainee, onClick }) {
  const pct = Math.round((trainee.completed_days / 16) * 100);
  return (
    <tr
      data-testid={`trainee-row-${trainee.id}`}
      onClick={onClick}
      className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer transition-colors"
    >
      <td className="py-3 px-4 font-body text-sm font-medium text-[#0A0A0A]">{trainee.name}</td>
      <td className="py-3 px-4 font-body text-sm text-[#4B5563]">@{trainee.username}</td>
      <td className="py-3 px-4 font-body text-sm text-[#4B5563]">{trainee.assigned_trainer_name || 'Unassigned'}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Progress value={pct} className="h-2 w-24" />
          <span className="font-body text-xs text-[#4B5563]">{trainee.completed_days}/16</span>
        </div>
      </td>
    </tr>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`)
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0047FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  const isAdmin = ['master_admin', 'admin'].includes(user?.role);
  const isManager = user?.role === 'manager';
  const isTrainer = user?.role === 'trainer';
  const isAgent = user?.role === 'agent';

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] tracking-tight">Dashboard</h1>
        <p className="font-body text-sm text-[#4B5563] mt-1">Welcome back, {user?.name}</p>
      </div>

      {/* Admin KPIs */}
      {isAdmin && stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KpiCard icon={Users} label="Total Users" value={stats.total_users || 0} />
            <KpiCard icon={Shield} label="Trainers" value={stats.trainer_count || 0} color="#F59E0B" />
            <KpiCard icon={UserCheck} label="Agents" value={stats.agent_count || 0} color="#16A34A" />
            <KpiCard icon={GraduationCap} label="Managers" value={stats.manager_count || 0} color="#8B5CF6" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-semibold text-[#0A0A0A] tracking-tight mb-4">Recent Trainees</h2>
            {stats.recent_trainees?.length > 0 ? (
              <div className="border border-[#E5E7EB] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Name</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Username</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Trainer</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_trainees.map(t => (
                      <TraineeRow key={t.id} trainee={t} onClick={() => navigate(`/trainees/${t.id}`)} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="font-body text-sm text-[#4B5563]">No trainees yet. Create agents in the Users page.</p>
            )}
          </div>
        </>
      )}

      {/* Manager KPIs */}
      {isManager && stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <KpiCard icon={Shield} label="Trainers" value={stats.trainer_count || 0} color="#F59E0B" />
            <KpiCard icon={UserCheck} label="Agents" value={stats.agent_count || 0} color="#16A34A" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-semibold text-[#0A0A0A] tracking-tight mb-4">Trainees Overview</h2>
            {stats.recent_trainees?.length > 0 ? (
              <div className="border border-[#E5E7EB] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Name</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Username</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Trainer</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_trainees.map(t => (
                      <TraineeRow key={t.id} trainee={t} onClick={() => navigate(`/trainees/${t.id}`)} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="font-body text-sm text-[#4B5563]">No trainees found.</p>
            )}
          </div>
        </>
      )}

      {/* Trainer view */}
      {isTrainer && stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <KpiCard icon={UserCheck} label="My Trainees" value={stats.agent_count || 0} color="#0047FF" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-semibold text-[#0A0A0A] tracking-tight mb-4">Assigned Trainees</h2>
            {stats.recent_trainees?.length > 0 ? (
              <div className="border border-[#E5E7EB] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Name</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Username</th>
                      <th className="py-3 px-4 text-left font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_trainees.map(t => (
                      <tr key={t.id} onClick={() => navigate(`/trainees/${t.id}`)} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer transition-colors">
                        <td className="py-3 px-4 font-body text-sm font-medium text-[#0A0A0A]">{t.name}</td>
                        <td className="py-3 px-4 font-body text-sm text-[#4B5563]">@{t.username}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Progress value={Math.round((t.completed_days / 16) * 100)} className="h-2 w-24" />
                            <span className="font-body text-xs text-[#4B5563]">{t.completed_days}/16</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="font-body text-sm text-[#4B5563]">No trainees assigned to you yet.</p>
            )}
          </div>
        </>
      )}

      {/* Agent view */}
      {isAgent && stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E5E7EB] p-6 col-span-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold text-[#0A0A0A] tracking-tight">My Training Progress</h2>
                <Badge variant="outline" className="rounded-none font-body text-xs">
                  Day {stats.current_day} of {stats.total_days}
                </Badge>
              </div>
              <Progress value={Math.round((stats.completed_days / stats.total_days) * 100)} className="h-3 mb-2" />
              <p className="font-body text-sm text-[#4B5563]">
                {stats.completed_days} of {stats.total_days} days completed ({Math.round((stats.completed_days / stats.total_days) * 100)}%)
              </p>
            </div>
          </div>

          {stats.recent_remarks?.length > 0 && (
            <div>
              <h2 className="font-heading text-xl font-semibold text-[#0A0A0A] tracking-tight mb-4">Recent Feedback</h2>
              <div className="space-y-3">
                {stats.recent_remarks.map((r, i) => (
                  <div key={i} className="border border-[#E5E7EB] p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-body text-sm font-semibold text-[#0A0A0A]">{r.author_name}</span>
                      <Badge variant="outline" className="rounded-none text-xs font-body capitalize">{r.remark_type}</Badge>
                    </div>
                    <p className="font-body text-sm text-[#4B5563]">{r.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
